import React, { useEffect, useState, useCallback } from "react";
import Pagination from "@mui/material/Pagination";
import { ColorRing } from "react-loader-spinner";
import { ArrowBack, Visibility, VisibilityOff } from "@mui/icons-material";
import API_BASE_URL from "../../../../Constants";
import ListHeader from "../../../../components/ListHeader";
import DirectorEntryForm from "./DirectorEntryForm"; // Entry form for adding/editing directors

const DirectorListFields = [
  { key: "actions", label: "Actions" },
  { key: "id", label: "ID" },
  { key: "name", label: "Name" },
  { key: "email", label: "Email" },
  { key: "password", label: "Password" },
];

const DirectorList = ({ onClose }) => {
  const [directors, setDirectors] = useState([]);
  const [filteredDirectors, setFilteredDirectors] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isEntryFormOpen, setIsEntryFormOpen] = useState(false);
  const [selectedDirector, setSelectedDirector] = useState(null); // For editing
  const [viewMode, setViewMode] = useState("table"); // Toggle between table and card views
  const [error, setError] = useState(null);
  // State for tracking visible (decrypted) passwords per director ID
  const [visiblePasswords, setVisiblePasswords] = useState({});

  // 1. Fetch Directors (paged)
  const fetchDirectors = useCallback(
    async (page = currentPage, size = rowsPerPage, query = "") => {
      try {
        setIsLoading(true);
        const queryString = query ? `&q=${encodeURIComponent(query)}` : "";
        const response = await fetch(
          `${API_BASE_URL}/api/setup/dobconnectdirectors?page=${
            page - 1
          }&size=${size}${queryString}`,
          { credentials: "include" }
        );
        if (!response.ok) throw new Error("Failed to fetch directors.");
        const data = await response.json();

        // data.content -> array of directors; other pagination info in data
        setDirectors(Array.isArray(data.content) ? data.content : []);
        setFilteredDirectors(Array.isArray(data.content) ? data.content : []);
        setCurrentPage(data.number + 1);
        setTotalPages(data.totalPages);
        setIsLoading(false);
      } catch (err) {
        setError(err.message);
        setIsLoading(false);
      }
    },
    [currentPage, rowsPerPage]
  );

  useEffect(() => {
    fetchDirectors(currentPage, rowsPerPage, searchQuery);
  }, [fetchDirectors, currentPage, rowsPerPage, searchQuery]);

  // 2. Search
  const handleSearch = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchDirectors(1, rowsPerPage, searchQuery);
  };

  // 3. Delete
  const handleDelete = async (id) => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/setup/dobconnectdirectors/${id}`,
        {
          method: "DELETE",
          credentials: "include",
        }
      );
      if (!response.ok) throw new Error("Failed to delete director.");
      // Refresh the list after deletion
      fetchDirectors(currentPage, rowsPerPage, searchQuery);
    } catch (err) {
      alert("Error deleting director. Please try again.");
    }
  };

  // 4. Edit / Add
  const handleEdit = (director) => {
    setSelectedDirector(director); // Populate form
    setIsEntryFormOpen(true);
  };

  const handleAddDirector = () => {
    setSelectedDirector(null); // New director
    setIsEntryFormOpen(true);
  };

  // 5. Close Modal
  const handleCloseEntryForm = () => {
    setIsEntryFormOpen(false);
  };

  // 6. Back button
  const handleBackButton = () => {
    console.log("Back button clicked. Calling onClose...");
    onClose && onClose();
  };

  // 7. Toggle View Password
  const togglePassword = async (directorId) => {
    // If already visible, hide it
    if (visiblePasswords[directorId]) {
      setVisiblePasswords((prev) => {
        const updated = { ...prev };
        delete updated[directorId];
        return updated;
      });
    } else {
      // Simulate API call to fetch decrypted password
      try {
        // Replace this with your real endpoint call as needed
        const simulatedResponse = await fetch(
          `${API_BASE_URL}/api/setup/dobconnectdirectors/${directorId}/view-password`,
          { credentials: "include" }
        ).then((res) => res.text());
        setVisiblePasswords((prev) => ({
          ...prev,
          [directorId]: simulatedResponse,
        }));
      } catch (err) {
        console.error(err);
        alert("Failed to fetch decrypted password.");
      }
    }
  };

  return (
    <div className="mx-auto p-4 h-90 w-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between py-4">
        {/* Back Arrow and Title */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handleBackButton}
            className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition"
            aria-label="Go Back"
          >
            <ArrowBack />
          </button>
          <h2 className="text-xl font-bold">Director List</h2>
        </div>

        {/* Search, Add, and View Mode */}
        <ListHeader
          title=""
          searchQuery={searchQuery}
          handleSearch={handleSearch}
          handleSearchChange={(e) => setSearchQuery(e.target.value)}
          viewMode={viewMode}
          handleViewModeChange={(mode) => setViewMode(mode)}
          handleOpenCreateModal={handleAddDirector}
        />
      </div>

      {/* Error Message */}
      {error && <div className="text-red-500 text-center my-2">{error}</div>}

      {/* Loading Indicator */}
      {isLoading && (
        <div className="flex justify-center items-center flex-grow">
          <ColorRing
            visible={isLoading}
            height="200"
            width="200"
            ariaLabel="color-ring-loading"
            wrapperStyle={{}}
            wrapperClass="color-ring-wrapper"
            colors={["#033B5C", "#033B5C", "#033B5C", "#033B5C", "#033B5C"]}
          />
        </div>
      )}

      {/* Table or Card View */}
      {!isLoading && (
        <>
          {viewMode === "table" ? (
            <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-200 mb-4">
              <thead className="bg-gray-200">
                <tr>
                  {DirectorListFields.map((field) => (
                    <th
                      key={field.key}
                      className="px-4 py-2 text-left font-bold text-sss-blue-sel"
                    >
                      {field.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-gray-700">
                {filteredDirectors.map((director) => (
                  <tr key={director.id} className="border-t">
                    <td className="px-4 py-2 space-x-2">
                      <button
                        onClick={() => handleEdit(director)}
                        className="text-blue-500 hover:underline"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(director.id)}
                        className="text-red-500 hover:underline"
                      >
                        Delete
                      </button>
                    </td>
                    {DirectorListFields.filter(
                      (field) => field.key !== "actions"
                    ).map((field) => (
                      <td key={field.key} className="px-4 py-2">
                        {field.key === "password" ? (
                          <button
                            onClick={() => togglePassword(director.id)}
                            className="flex items-center space-x-1"
                          >
                            {visiblePasswords[director.id] ? (
                              <>
                                <span>{visiblePasswords[director.id]}</span>
                                <VisibilityOff fontSize="small" />
                              </>
                            ) : (
                              <Visibility fontSize="small" />
                            )}
                          </button>
                        ) : (
                          director[field.key] || "N/A"
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredDirectors.map((director) => (
                <div
                  key={director.id}
                  className="p-4 bg-white rounded-lg shadow-md border"
                >
                  <div className="mb-2">
                    <strong>ID:</strong> {director.id}
                  </div>
                  <div className="mb-2">
                    <strong>Name:</strong> {director.name}
                  </div>
                  <div className="mb-2">
                    <strong>Email:</strong> {director.email}
                  </div>
                  <div className="mb-2 flex items-center space-x-1">
                    <strong>Password:</strong>
                    <button onClick={() => togglePassword(director.id)}>
                      {visiblePasswords[director.id] ? (
                        <VisibilityOff fontSize="small" />
                      ) : (
                        <Visibility fontSize="small" />
                      )}
                    </button>
                    {visiblePasswords[director.id] && (
                      <span>{visiblePasswords[director.id]}</span>
                    )}
                  </div>
                  <div className="flex justify-end space-x-2 mt-2">
                    <button
                      onClick={() => handleEdit(director)}
                      className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(director.id)}
                      className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Pagination */}
      <div className="flex justify-between items-center p-4 mt-auto border-t border-gray-300 bg-white">
        <Pagination
          count={totalPages}
          page={currentPage}
          onChange={(event, value) => setCurrentPage(value)}
          color="primary"
          shape="rounded"
        />
        <div className="flex items-center">
          <label htmlFor="rows-per-page" className="mr-2">
            Rows per page:
          </label>
          <select
            id="rows-per-page"
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
              fetchDirectors(1, Number(e.target.value), searchQuery);
            }}
            className="border border-gray-300 rounded p-2"
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Director Entry Form Modal */}
      <DirectorEntryForm
        open={isEntryFormOpen}
        onClose={handleCloseEntryForm}
        directorData={selectedDirector}
        /* Pass fetchDirectors as refresh callback */
        refreshDirectors={() =>
          fetchDirectors(currentPage, rowsPerPage, searchQuery)
        }
      />
    </div>
  );
};

export default DirectorList;
